import {
  computed,
  onBeforeMount,
  reactive,
  Ref,
  ref,
  toRefs,
  useContext,
} from "@nuxtjs/composition-api"

import {
  SearchTermParams,
  useAutocompleteQuery,
  useGeolocationsQuery,
  useWorkplacesAutocompleteQuery,
} from "@/api/query/geolocationsQuery"
import { Coordinates } from "@/types/models/Coordinates"

function useGeolocation() {
  const { i18n } = useContext()
  const coordinates = reactive<Coordinates>({ latitude: NaN, longitude: NaN })
  const isGeolocationAllowed = ref<boolean>()

  const isGeolocationAvailable =
    "navigator" in window && "geolocation" in navigator
  const getCurrentGeolocation = () => {
    if (isGeolocationAvailable) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          coordinates.latitude = position.coords.latitude
          coordinates.longitude = position.coords.longitude
          isGeolocationAllowed.value = true
        },
        () => {
          isGeolocationAllowed.value = false
        },
      )
    }
  }

  const coordinatesSync = computed(() => {
    return (
      (isGeolocationAllowed.value && !Number.isNaN(coordinates.latitude)) ||
      isGeolocationAllowed.value === false
    )
  })

  const locationAutocompleteFilters = reactive<SearchTermParams>({
    searchTerm: "",
  })

  const searchTermGeolocation = () => {
    const { data: results, isFetching: isFetchingAutocomplete } =
      useAutocompleteQuery(
        reactive({
          ...toRefs(locationAutocompleteFilters),
          ...toRefs(coordinates),
          language: computed(() => i18n.locale),
        }),
        reactive({
          enabled: computed(() =>
            Boolean(locationAutocompleteFilters.searchTerm),
          ),
        }),
      )
    return { results, isFetchingAutocomplete }
  }

  const searchTermWorkplaces = () => {
    const {
      data: resultsWorkplaces,
      isFetching: isFetchingWorkplacesAutocomplete,
    } = useWorkplacesAutocompleteQuery(
      reactive({
        ...toRefs(locationAutocompleteFilters),
        ...toRefs(coordinates),
      }),
    )
    return { resultsWorkplaces, isFetchingWorkplacesAutocomplete }
  }

  const getGeolocations = (placeIds: Ref<string[]>) => {
    const { data: geolocations, isFetching: isFetchingGeolocations } =
      useGeolocationsQuery(
        reactive({
          placeIds,
          language: computed(() => i18n.locale),
        }),
      )
    return { geolocations, isFetchingGeolocations }
  }

  onBeforeMount(() => {
    getCurrentGeolocation()
  })

  return {
    coordinates,
    coordinatesSync,
    getGeolocations,
    getCurrentGeolocation,
    searchTermWorkplaces,
    searchTermGeolocation,
    locationAutocompleteFilters,
    isGeolocationAllowed,
  }
}
export default useGeolocation
